import { cn } from "@/utils/cn";
import { forwardRef, type ButtonHTMLAttributes, type PointerEvent } from "react";

const variants = {
  primary:
    "bg-primary text-primary-foreground hover:bg-primary-hover shadow-sm hover:shadow-glow",
  secondary:
    "bg-secondary text-secondary-foreground hover:bg-muted border border-border",
  accent:
    "bg-accent text-accent-foreground hover:opacity-90 shadow-sm",
  outline:
    "border border-border bg-transparent hover:bg-muted text-foreground",
  ghost:
    "hover:bg-muted text-foreground",
  destructive:
    "bg-destructive text-destructive-foreground hover:opacity-90",
} as const;

const sizes = {
  sm: "h-8 px-3 text-sm gap-1.5",
  md: "h-10 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
  icon: "h-10 w-10",
} as const;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
};

/** A ripple from where the button was pressed (styles: .btn-ripple in globals.css; none for reduced motion). */
function spawnRipple(e: PointerEvent<HTMLButtonElement>) {
  const button = e.currentTarget;
  const rect = button.getBoundingClientRect();
  const size = Math.max(rect.width, rect.height) * 2;
  const ripple = document.createElement("span");
  ripple.className = "btn-ripple";
  ripple.style.width = ripple.style.height = `${size}px`;
  ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
  ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
  button.appendChild(ripple);
  ripple.addEventListener("animationend", () => ripple.remove());
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", onPointerDown, ...props }, ref) => (
    <button
      ref={ref}
      onPointerDown={(e) => {
        spawnRipple(e);
        onPointerDown?.(e);
      }}
      className={cn(
        // Pressed buttons sink a little; `relative overflow-hidden` keeps the ripple inside.
        "relative inline-flex items-center justify-center overflow-hidden rounded-lg font-medium transition-all duration-150 active:scale-[0.96]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:pointer-events-none disabled:opacity-50",
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  ),
);

Button.displayName = "Button";
